// @flow
import Place from "../../records/Place"
import type { City } from "../../records/Place"
import type { Location } from "./locationTypes"
import { locationToPlaceTypes } from "./placeLocationMapper"

type Geo = { lat: ?number, lng: ?number }

function getGeo(location: Location): Geo {
  return location.location
    ? { lat: location.location.lat, lng: location.location.lon }
    : { lat: null, lng: null }
}

function getFlagParentId(location: Location): ?string {
  switch (location.type) {
    case "airport":
    case "station":
    case "bus_station":
      return location.city ? location.city.id : null
    case "city":
      if (location.autonomous_territory) {
        return location.autonomous_territory.id
      }
      return location.country ? location.country.id : null

    case "subdivision":
    case "autonomous_territory":
      return location.country ? location.country.id : null

    default:
      return null
  }
}

function getParentIds(location: Location): Array<string> {
  const parentIds = []

  switch (location.type) {
    case "airport":
    case "station":
    case "bus_station":
      parentIds.push(
        location,
        location.city,
        location.city.autonomous_territory,
        location.city.subdivision,
        location.city.country,
        location.city.region,
        location.city.continent,
      )
      break

    case "city":
      parentIds.push(
        location,
        location.subdivision,
        location.autonomous_territory,
        location.country,
        location.region,
        location.continent,
      )
      break

    case "subdivision":
      parentIds.push(location, location.country, location.region, location.continent)
      break

    case "autonomous_territory":
      parentIds.push(location, location.country, location.region, location.continent)
      break

    case "country":
      parentIds.push(location, location.region, location.continent)
      break

    case "region":
      parentIds.push(location, location.continent)
      break

    default:
      break
  }

  return parentIds.filter(Boolean).map(t => t.id)
}

function getSuffix(location: Location): string {
  switch (location.type) {
    case "autonomous_territory":
    case "city":
      return location.country ? ` (${location.country.name})` : ""
    case "airport":
    case "station":
    case "bus_station":
      return ` ${location.id}`
    default:
      return ""
  }
}

function getPopulation(location: Location): ?number {
  if (location.type === "city") {
    return location.population
  }

  return null
}

function getCity(location: Location): ?City {
  if (
    location.type === "airport" ||
    location.type === "station" ||
    location.type === "bus_station"
  ) {
    return { name: location.city.name, id: location.city.id }
  }

  return null
}

function getCountry(location: Location): ?string {
  switch (location.type) {
    case "airport":
    case "station":
    case "bus_station":
      return location.city.country && location.city.country.id
    case "city":
    case "subdivision":
    case "autonomous_territory":
      return location.country && location.country.id
    default:
      return null
  }
}

function getCountryName(location: Location): ?string {
  switch (location.type) {
    case "airport":
    case "station":
    case "bus_station":
      return location.city.country && location.city.country.name
    case "city":
    case "subdivision":
    case "autonomous_territory":
      return location.country && location.country.name
    default:
      return null
  }
}

function getValue(location) {
  const suffix = getSuffix(location)
  return (
    location.name +
    suffix +
    (suffix && /[a-zA-Z]+/.test(location.name) // latin strings have problem with closing parentheses in rtl languages, so we have to add ltr marker
      ? "\u200e"
      : "")
  )
}

const mapLocationToPlace = (location: Location): Place => {
  const { lat, lng } = getGeo(location)
  return new Place({
    id: location.id,
    flagParentId: getFlagParentId(location),
    parentIds: getParentIds(location),
    value: getValue(location), // whole name with brackets
    slug: location.slug,
    lat,
    lng,
    continent: location.continent ? location.continent.name : null,
    type: locationToPlaceTypes[location.type], // type in integer
    population: getPopulation(location),
    complete: true, // TODO ??? for backward compatibility, if false it will fetch additional data from API
    rank: location.rank,
    city: getCity(location),
    country: getCountry(location),
    code: location.code || location.id,
    name: location.name,
    // $FlowFixMe: no idea why I have to do this, airports is a prop in City type
    numberOfAirports: (location.airports || 0) + (location.stations || 0),
    countryName: getCountryName(location),
    locationsNearby: location.type === "special" ? location.locations_nearby : null,
    tags: location.type === "city" ? location.tags : null,
    neighbours: location.type === "country" ? location.neighbours : null,
  })
}

export default mapLocationToPlace
